import { ActionIcon, Box, Button, Drawer, LoadingOverlay, Text, modals, useForm, LoadError, NoAccess } from "@dehub/theme";
import { CheckCircleIcon, XIcon } from "@phosphor-icons/react";
import { yupResolver } from "mantine-form-yup-resolver";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { IAuth } from "@/models/Auth";
import { BusinessApi, GeneralApi } from "@/apis";
import { initGeneral } from "@/store/General";
import { formatDate } from "@/utils/format-date";
import { message } from "@/utils/message";
import { renderBusinessName } from "@/utils/render-business-name";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission, TransCode } from "@/shared/a11y/trans-code";
import { FormSkeleton } from "../components/FormSkeleton";
import type { BusinessPermissions } from "../permissions";
import type { BusinessRecord, BusinessType, DrawerMode } from "../types";
import {
  DEFAULT_EXTRAS,
  DEFAULT_MARKETING,
  DEFAULT_VALUES,
  buildPayload,
  buildSchema,
  ensureOneMain,
  isMarketingEqual,
  toExtras,
  toFormValues,
  toMarketing,
  type BusinessExtras,
  type BusinessFormValues,
  type MarketingState,
} from "./business-form-model";
import { InfoStep } from "./InfoStep";
import { MarketingStep } from "./MarketingStep";
import { ReviewStep } from "./ReviewStep";
import { StepIndicator, type StepNumber } from "./StepIndicator";
import classes from "../business.module.css";

type Props = Readonly<{
  opened: boolean;
  mode: DrawerMode;
  type: BusinessType;
  record?: BusinessRecord;
  perms: BusinessPermissions;
  onClose: () => void;
  /** The list should reload: something was saved. */
  onChanged: () => void;
  /** Reports whether the drawer holds unsaved edits (the page guards navigation with it). */
  onDirtyChange?: (dirty: boolean) => void;
}>;

type CreatedBusiness = { id: string; refCode?: string | null; createdAt?: string | null };

export function BusinessDrawer({ opened, mode, type, record, perms, onClose, onChanged, onDirtyChange }: Props) {
  const { t } = useTranslation("business");
  const { t: tCommon } = useTranslation("common");
  const dispatch = useDispatch();
  const hasAddress = Boolean(useSelector((state: { auth: IAuth }) => state.auth.user?.partner?.province));
  const schema = useMemo(() => buildSchema(tCommon), [tCommon]);
  const form = useForm<BusinessFormValues>({ initialValues: DEFAULT_VALUES, validate: yupResolver(schema) });

  const [step, setStep] = useState<StepNumber>(1);
  const [businessId, setBusinessId] = useState<string | null>(record?.id ?? null);
  const [loading, setLoading] = useState(Boolean(record?.id));
  // The load error text, or null. While set, nothing here may save: the form would be empty.
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(Boolean(record?.isConfirmed));
  const [done, setDone] = useState<ReadonlySet<StepNumber>>(() => new Set<StepNumber>(record?.id ? [1] : []));
  const [extras, setExtras] = useState<BusinessExtras>(DEFAULT_EXTRAS);
  const [marketing, setMarketing] = useState<MarketingState>(DEFAULT_MARKETING);
  const [marketingBase, setMarketingBase] = useState<MarketingState>(DEFAULT_MARKETING);
  const [marketingVersion, setMarketingVersion] = useState(0);
  const [meta, setMeta] = useState({ refCode: record?.refCode ?? null, createdAt: record?.createdAt ?? null });

  const detailMode = mode === "DETAIL";
  // Once confirmed the main information is locked; marketing images stay editable (with permission).
  const infoReadOnly = detailMode || confirmed;
  const marketingDirty = !isMarketingEqual(marketing, marketingBase);
  const hasChanges = form.isDirty() || marketingDirty;
  const busy = loading || saving;
  // State updates are async: a ref stops a second click from starting another save in the same tick.
  const savingRef = useRef(false);

  // An existing business is loaded in full: the row only carries a few columns, so a form seeded
  // from it would start empty and a save would overwrite the stored latin name, introduction, VAT.
  const [loadAttempt, setLoadAttempt] = useState(0);
  useEffect(() => {
    if (!record?.id) return;
    let cancelled = false;
    setLoading(true);
    setLoadFailed(null);
    BusinessApi.get(record.id)
      .then((data: Record<string, any>) => {
        if (cancelled) return;
        const values = toFormValues(data);
        form.setInitialValues(values);
        form.setValues(values);
        form.resetDirty(values);
        setExtras(toExtras(data));
        const loaded = toMarketing(data);
        setMarketing(loaded);
        setMarketingBase(loaded);
        setMarketingVersion((v) => v + 1);
        setConfirmed(Boolean(data?.isConfirmed ?? record.isConfirmed));
        setMeta({ refCode: data?.refCode ?? record.refCode ?? null, createdAt: data?.createdAt ?? record.createdAt ?? null });
      })
      .catch((error: { message?: string }) => {
        if (cancelled) return;
        setLoadFailed(error?.message ?? tCommon("error.generic"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, loadAttempt]);

  useEffect(() => {
    onDirtyChange?.(opened && hasChanges);
    return () => onDirtyChange?.(false);
  }, [opened, hasChanges, onDirtyChange]);

  const markDone = (n: StepNumber) => setDone((prev) => new Set<StepNumber>(Array.from(prev).concat(n)));

  const isReachable = (n: StepNumber) => n === 1 || (!loadFailed && (detailMode || Boolean(businessId)));

  const focusFirstError = (errors: Record<string, unknown>) => {
    const key = Object.keys(errors)[0] as keyof BusinessFormValues | undefined;
    if (!key) return;
    const node = form.getInputNode?.(key);
    node?.scrollIntoView({ behavior: "smooth", block: "center" });
    node?.focus?.();
  };

  /** Validates the main information; on an error the drawer returns to step 1 and focuses the field. */
  const validateInfo = (): boolean => {
    const result = form.validate();
    if (!result.hasErrors) return true;
    if (step !== 1) setStep(1);
    setTimeout(() => focusFirstError(result.errors), 80);
    return false;
  };

  const persistInfo = async (confirm: boolean): Promise<boolean> => {
    if (savingRef.current) return false;
    if (!validateInfo()) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = buildPayload(type, form.values, extras, confirm, !businessId);
      let successKey = "form.saveSuccess";
      if (businessId) {
        await BusinessApi.update(businessId, payload);
      } else {
        const created = (await BusinessApi.create(payload)) as CreatedBusiness;
        setBusinessId(created.id);
        setMeta((prev) => ({ refCode: created.refCode ?? prev.refCode, createdAt: created.createdAt ?? prev.createdAt }));
        successKey = "form.createSuccess";
      }
      message.success(t(confirm ? "form.confirmSuccess" : successKey));
      form.setInitialValues(form.values);
      form.resetDirty(form.values);
      if (confirm) setConfirmed(true);
      onChanged();
      try {
        dispatch(initGeneral(await GeneralApi.init()));
      } catch {
        // the saved business is already in; a stale selector list is not worth failing the save
      }
      return true;
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message ?? tCommon("error.generic"));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const persistMarketing = async (): Promise<boolean> => {
    if (!businessId || savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      // The backend rejects a banner list without exactly one main banner: normalise right before sending.
      const banners = ensureOneMain(marketing.banners);
      await BusinessApi.profile(businessId, {
        profileBanners: banners.length > 0 ? banners : null,
        logo: marketing.logo,
      });
      message.success(t("marketing.saveSuccess"));
      setMarketingBase(marketing);
      onChanged();
      return true;
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message ?? tCommon("error.generic"));
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const infoNeedsSave = !infoReadOnly && (!businessId || form.isDirty());
  // Saving existing information is PUT /prt/business/:id (EDIT); the form itself is MAIN. A missing
  // permission blocks the button and names the code, rather than skipping the save.
  const infoBlockedCode: string | null = infoReadOnly
    ? null
    : !perms.main.hasAccess
      ? perms.codes.main
      : businessId && form.isDirty() && !perms.businessEdit.hasAccess
        ? perms.codes.businessEdit
        : null;
  const confirmBlockedCode: string | null = !perms.main.hasAccess
    ? perms.codes.main
    : !perms.businessEdit.hasAccess
      ? perms.codes.businessEdit
      : null;

  const saveInfoIfNeeded = async (): Promise<boolean> => {
    if (!infoNeedsSave) return true;
    if (infoBlockedCode) return false;
    return persistInfo(false);
  };

  const saveMarketingIfNeeded = async (): Promise<boolean> => {
    if (!marketingDirty || !businessId || !perms.marketing.hasAccess) return true;
    return persistMarketing();
  };

  const handleConfirm = () => {
    if (confirmBlockedCode || infoReadOnly) return;
    modals.openConfirmModal({
      withCloseButton: false,
      centered: true,
      radius: "lg",
      title: (
        <Box component="span" className={`${classes.tokens} ${classes.confirmTitleRow}`}>
          <Box component="span" className={`${classes.confirmIcon} ${classes.confirmIconOn}`}>
            <CheckCircleIcon size={20} weight="bold" aria-hidden="true" />
          </Box>
          <Text component="span" className={classes.confirmTitle}>
            {t("review.confirmTitle")}
          </Text>
        </Box>
      ),
      children: <Text className={`${classes.tokens} ${classes.confirmText}`}>{t("form.confirmText")}</Text>,
      labels: { confirm: tCommon("confirm.confirm"), cancel: tCommon("confirm.back") },
      confirmProps: { color: "green", className: classes.btn },
      cancelProps: { className: classes.btn },
      onConfirm: async () => {
        if (!(await saveMarketingIfNeeded())) return;
        if (await persistInfo(true)) onClose();
      },
    });
  };

  const handleNext = async () => {
    if (savingRef.current) return;
    if (step === 1) {
      // Step 1 always checks the required fields, even when nothing changed.
      if (!infoReadOnly && !validateInfo()) return;
      if (!(await saveInfoIfNeeded())) return;
      markDone(1);
      setStep(2);
    } else if (step === 2) {
      if (!(await saveMarketingIfNeeded())) return;
      markDone(2);
      setStep(3);
    } else {
      handleConfirm();
    }
  };

  const handleDraftExit = async () => {
    if (savingRef.current) return;
    if (!(await saveInfoIfNeeded())) return;
    if (!(await saveMarketingIfNeeded())) return;
    onClose();
  };

  const requestClose = () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    modals.openConfirmModal({
      title: t("drawer.discardTitle"),
      children: <Text fz={15}>{t("drawer.discardText")}</Text>,
      labels: { confirm: t("drawer.discard"), cancel: tCommon("actions.back") },
      confirmProps: { color: "red", className: classes.btn },
      cancelProps: { className: classes.btn },
      centered: true,
      onConfirm: onClose,
    });
  };

  const typeFull = t(`typeFull.${type}`);
  const isNew = !businessId;
  const displayName = renderBusinessName({ profileName: form.values.profileName, profileNameEng: form.values.profileNameEng }, "");
  const kicker = isNew ? t("drawer.addKicker", { type: typeFull }) : typeFull;
  const title = displayName || (isNew ? t("drawer.newTitle") : renderBusinessName(record ? { profileName: record.profileName, profileNameEng: record.profileNameEng } : null));

  const primaryLabel = step === 3 ? tCommon("confirm.confirm") : t("drawer.saveContinue");
  const blockedCode = step === 1 ? infoBlockedCode : step === 3 ? confirmBlockedCode : null;
  const draftBlocked = infoNeedsSave && Boolean(infoBlockedCode);
  const blockedReason = blockedCode ? <MissingPermission code={blockedCode} /> : null;
  // Every disabled control says why: a permission code, an unloaded record, or "already confirmed".
  const primaryReason = loadFailed
    ? t("drawer.loadFailedTitle")
    : blockedReason ?? (step === 3 && infoReadOnly ? t("review.alreadyConfirmed") : null);
  const draftReason = loadFailed ? t("drawer.loadFailedTitle") : draftBlocked ? blockedReason : null;

  const renderStep = () => {
    if (loading) return <FormSkeleton />;
    if (loadFailed) {
      return (
        <LoadError
          title={t("drawer.loadFailedTitle")}
          message={`${loadFailed} ${t("drawer.loadFailedText")}`}
          retryLabel={tCommon("actions.retry")}
          onRetry={() => setLoadAttempt((n) => n + 1)}
        />
      );
    }
    if (step === 1) {
      return perms.main.hasAccess ? (
        <InfoStep form={form} readOnly={infoReadOnly} />
      ) : (
        <NoAccess title={t("drawer.noAccessTitle")} description={<TransCode i18nKey="business:drawer.noAccessText" values={{ code: perms.codes.main }} />} />
      );
    }
    if (step === 2) {
      if (!businessId) {
        return <NoAccess title={t("steps.lockedTitle")} description={t("steps.lockedText")} />;
      }
      return perms.marketing.hasAccess ? (
        <MarketingStep value={marketing} version={marketingVersion} businessId={businessId} onChange={setMarketing} />
      ) : (
        <NoAccess title={t("drawer.noAccessTitle")} description={<TransCode i18nKey="business:drawer.noAccessText" values={{ code: perms.codes.marketing }} />} />
      );
    }
    return <ReviewStep values={form.values} marketing={marketing} confirmed={confirmed} />;
  };

  return (
    <Drawer
      opened={opened}
      onClose={requestClose}
      position="right"
      size="min(720px, 100%)"
      padding={0}
      withCloseButton={false}
      classNames={{ content: `${classes.tokens} ${classes.drawerRoot}`, body: classes.drawerBody }}
    >
      <Box className={classes.drawerHead}>
        <Box className={classes.drawerHeadRow}>
          <Box>
            <Text component="span" display="block" className={classes.drawerKicker}>
              {kicker}
              {!isNew && meta.refCode ? (
                <>
                  {" · "}
                  <Text component="span" className={classes.mono} translate="no">
                    {meta.refCode}
                  </Text>
                </>
              ) : null}
              {!isNew && meta.createdAt ? ` · ${formatDate(meta.createdAt)}` : null}
            </Text>
            <Drawer.Title className={classes.drawerTitle}>
              {title}
            </Drawer.Title>
          </Box>
          <ActionIcon variant="subtle" color="gray" size={34} className={classes.closeButton} aria-label={tCommon("actions.close")} onClick={requestClose}>
            <XIcon size={16} weight="bold" />
          </ActionIcon>
        </Box>
        <StepIndicator current={step} done={done} isReachable={isReachable} onGo={setStep} />
      </Box>

      <Box className={classes.drawerContent} pos="relative">
        <LoadingOverlay visible={saving} />
        {renderStep()}
      </Box>

      <Box className={classes.drawerFoot}>
        <Button variant="default" onClick={requestClose}>
          {detailMode ? tCommon("actions.close") : tCommon("actions.cancel")}
        </Button>
        {detailMode ? (
          step === 2 && perms.marketing.hasAccess && marketingDirty ? (
            <ReasonTooltip reason={busy || hasAddress ? null : t("addressError")}>
              <Button color="brand" disabled={busy} loading={saving} onClick={() => persistMarketing()}>
                {tCommon("actions.save")}
              </Button>
            </ReasonTooltip>
          ) : null
        ) : (
          <Box className={classes.drawerFootGroup}>
            {step > 1 ? (
              <Button variant="default" disabled={busy} onClick={() => setStep((step - 1) as StepNumber)}>
                {tCommon("actions.back")}
              </Button>
            ) : null}
            <ReasonTooltip reason={busy ? null : draftReason}>
              <Button variant="default" disabled={busy} loading={saving} onClick={handleDraftExit}>
                {t("drawer.saveDraft")}
              </Button>
            </ReasonTooltip>
            <ReasonTooltip reason={busy ? null : primaryReason}>
              <Button color="brand" disabled={busy} loading={saving} onClick={handleNext}>
                {primaryLabel}
              </Button>
            </ReasonTooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
